import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { apiFetch } from "@/lib/api";
import type { SignOutResult } from "@/lib/api-types";
import { CURRENT_ACTOR_QUERY_KEY } from "@/lib/current-actor";

/**
 * Gap-fill (frontend auth UI) — the one real sign-out call, shared by
 * `NavRail` (desktop) and `MoreSheet` (phone, where `NavRail` itself is
 * `md:hidden` and so was never reachable at all). `POST /auth/sign-out`
 * clears the session server-side; invalidating `CURRENT_ACTOR_QUERY_KEY`
 * (rather than trusting the click to have worked) is what makes
 * `useCurrentActor()` — and every Read-only/Owner check sharing its cache —
 * reflect signed-out on the very next render (VC-GF1-08). The `finally`
 * invalidates and navigates to `/sign-in` even if the network call itself
 * failed, so a lost connection during sign-out never strands someone on a
 * control that looks like it did nothing.
 *
 * Navigate first, and defer clearing the cached actor to a fresh macrotask
 * — the identical fix `IdleSessionGuard.tsx`'s own `handleTimeout` carries,
 * and the identical race found live while building it (Story 15.7):
 * `navigate()` only *schedules* the route change, so a same-tick cache
 * write right after it can still reach `RequireAuth` (and every screen this
 * hook is called from — `NavRail`, `MoreSheet` — while its tree has not
 * actually unmounted yet). When that happens, `RequireAuth`'s *own* generic
 * 401 redirect (`?redirect=<path>`, `RequireAuth.tsx`) reacts to the
 * freshly-401ing actor query and races this hook's own explicit,
 * unparameterized `/sign-in` navigate. The two happened to be
 * indistinguishable here (neither carries a query param anyone reads),
 * which is exactly why this shipped unnoticed — the race is still real and
 * nondeterministic, and would silently bite the moment any caller ever
 * needs a specific redirect target or message on manual sign-out too. A
 * `setTimeout(0)` — a new macrotask, strictly after the browser's own next
 * paint/event-loop turn — reliably runs after React has processed the
 * navigation and torn the signed-in tree down, so there is no live observer
 * left for the deferred cache write to reach.
 *
 * `removeQueries`, not `invalidateQueries` (found live, 2026-09-11: this
 * hook still carried the exact stale shape `IdleSessionGuard.tsx`'s own
 * "Owner-caught, 2026-08-28" comment already describes fixing there, never
 * back-ported here despite this docstring's own claim of carrying "the
 * identical fix"). `invalidateQueries()` only marks the query stale and
 * schedules a background refetch — it does not clear the *previous*
 * successful `data` from the cache until that refetch resolves. `SignIn.
 * tsx` itself calls `useCurrentActor()` (to bounce away if already signed
 * in) the moment it mounts, which is exactly what `navigate()` above
 * causes — and it mounts fast enough to read that still-cached, now-stale
 * "signed in" `data` before the deferred refetch has resolved, sending it
 * straight back through `<Navigate to={destination}>` to a screen that
 * immediately 401s on every request: a blank page and a real, non-
 * terminating render loop (`RequireAuth`'s tree re-mounting, re-401ing, and
 * `SignIn` re-reading the same still-resolving stale query) rather than the
 * sign-in form. `removeQueries` evicts the cached value immediately and
 * synchronously instead of scheduling a refetch against it — the freshly-
 * mounted `SignIn` observes a genuinely empty query (`data: undefined`,
 * `isPending`), never a stale success, so its own already-correct `if
 * (currentActor.data)` check has nothing false-positive to fire on.
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);

  async function signOut() {
    setIsSigningOut(true);
    try {
      await apiFetch<SignOutResult>("/auth/sign-out", { method: "POST" });
    } finally {
      setIsSigningOut(false);
      navigate("/sign-in", { replace: true });
      setTimeout(() => {
        queryClient.removeQueries({ queryKey: CURRENT_ACTOR_QUERY_KEY, exact: true });
      }, 0);
    }
  }

  return { isSigningOut, signOut };
}
